import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { pixelTrack } from "@/lib/pixel";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Crown, Zap, Sparkles, X, Building2, ArrowUpRight, ArrowDownRight, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

// Rank is used to compute upgrade vs downgrade. Higher rank = higher tier.
const PLAN_RANK = { free: 0, beginner_monthly: 1, pro_monthly: 2 };

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) { resolve(true); return; }
    const s = document.createElement("script");
    s.id = "razorpay-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const PLAN_FEATURES = {
  free: {
    icon: Zap,
    color: "border-slate-700",
    accent: "text-slate-400",
    features: [
      "10 one-time signup credits",
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Watermarked videos",
    ],
    notIncluded: ["Monthly credits", "Hero Shots", "Wallet top-up", "Priority queue"],
  },
  beginner_monthly: {
    icon: Sparkles,
    color: "border-blue-600",
    accent: "text-blue-400",
    imageEquiv: 25,
    videoEquiv: 5,
    features: [
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Wallet top-up enabled - ₹10/Credit",
    ],
    notIncluded: ["Hero Shots", "Category Explainer Videos", "Priority queue"],
  },
  pro_monthly: {
    icon: Crown,
    color: "border-amber-500",
    accent: "text-amber-400",
    imageEquiv: 100,
    videoEquiv: 20,
    features: [
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Hero Shots",
      "Category Explainer Videos",
      "Wallet top-up enabled - ₹10/Credit",
      "Priority queue",
    ],
    notIncluded: [],
  },
};

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [currentRole, setCurrentRole] = useState("free");
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  // Confirmation modal: { type: "upgrade"|"downgrade"|"cancel", targetPlanKey, targetPlanName, currentPlanName, currentCredits }
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, statusRes] = await Promise.all([
          axios.get(`${API}/api/subscriptions/plans`, { withCredentials: true }),
          axios.get(`${API}/api/subscriptions/status`, { withCredentials: true }),
        ]);
        setPlans(plansRes.data.plans || []);
        setSubscription(statusRes.data.subscription);
        setCurrentRole(statusRes.data.role || "free");
      } catch (err) {
        toast.error("Failed to load plans");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Core Razorpay checkout opener — shared by subscribe and change-plan flows
  const openRazorpayCheckout = (subData, planName, themeColor, onSuccessMessage) => {
    const options = {
      key: subData.key_id || RAZORPAY_KEY_ID,
      subscription_id: subData.subscription_id,
      name: "Prysh AI",
      description: planName,
      handler: async () => {
        pixelTrack("Subscribe", { predicted_ltv: 0, currency: "INR" });
        toast.success(onSuccessMessage);
        setTimeout(async () => {
          if (refreshUser) await refreshUser();
          window.location.reload();
        }, 3000);
      },
      prefill: { email: user?.email || "", name: user?.name || "" },
      theme: { color: themeColor },
      modal: {
        ondismiss: () => {
          setSubscribing(null);
          setChangingPlan(false);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    pixelTrack("InitiateCheckout", { content_name: planName, currency: "INR" });
    rzp.on("payment.failed", (resp) => {
      toast.error(resp.error?.description || "Payment failed");
      setSubscribing(null);
      setChangingPlan(false);
    });
    rzp.open();
  };

  const handleSubscribe = async (planKey) => {
    setSubscribing(planKey);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setSubscribing(null);
        return;
      }

      const res = await axios.post(
        `${API}/api/subscriptions/create`,
        { plan_key: planKey },
        { withCredentials: true }
      );

      openRazorpayCheckout(
        res.data,
        `${planKey === "pro_monthly" ? "Pro" : "Basic"} Monthly Plan`,
        planKey === "pro_monthly" ? "#d97706" : "#2563eb",
        "Subscription activated! Your account is being upgraded..."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create subscription");
      setSubscribing(null);
    }
  };

  const handleChangePlan = async (targetPlanKey) => {
    setChangingPlan(true);
    setSubscribing(targetPlanKey);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setChangingPlan(false);
        setSubscribing(null);
        return;
      }

      const res = await axios.post(
        `${API}/api/subscriptions/change-plan`,
        { target_plan_key: targetPlanKey },
        { withCredentials: true }
      );

      const changeLabel = res.data.change_type === "upgrade" ? "Upgrade" : "Downgrade";
      openRazorpayCheckout(
        res.data,
        `${targetPlanKey === "pro_monthly" ? "Pro" : "Basic"} Monthly Plan (${changeLabel})`,
        targetPlanKey === "pro_monthly" ? "#d97706" : "#2563eb",
        `Plan ${changeLabel.toLowerCase()} successful! Updating your account...`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change plan");
      setChangingPlan(false);
      setSubscribing(null);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/api/subscriptions/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message || "Subscription cancelled");
      setTimeout(async () => {
        if (refreshUser) await refreshUser();
        window.location.reload();
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
      setCancelling(false);
    }
  };

  // Called when user clicks a plan card CTA. Routes to subscribe, upgrade, or downgrade flow.
  const handlePlanAction = (planKey, action) => {
    if (action === "subscribe") {
      handleSubscribe(planKey);
    } else if (action === "upgrade" || action === "downgrade") {
      // Show confirmation modal before opening Razorpay checkout
      const targetName = planKey === "pro_monthly" ? "Pro" : "Beginner";
      const currentName = subscription?.plan_key === "pro_monthly" ? "Pro" : "Beginner";
      setConfirmModal({
        type: action,
        targetPlanKey: planKey,
        targetPlanName: targetName,
        currentPlanName: currentName,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isCurrentPlan = (planKey) => {
    if (planKey === "free") return currentRole === "free";
    return subscription?.plan_key === planKey && ["active", "authenticated"].includes(subscription?.status);
  };

  // Returns the action CTA for a given plan card given the user's current state.
  // One of: "current", "subscribe", "upgrade", "downgrade", "included" (for free card)
  const getPlanAction = (planKey) => {
    if (isCurrentPlan(planKey)) return "current";
    if (planKey === "free") return "included";

    const hasActiveSub =
      subscription?.status === "active" || subscription?.status === "authenticated";
    if (!hasActiveSub) return "subscribe";

    const currentRank = PLAN_RANK[subscription?.plan_key] ?? 0;
    const targetRank = PLAN_RANK[planKey] ?? 0;
    if (targetRank > currentRank) return "upgrade";
    if (targetRank < currentRank) return "downgrade";
    return "current";
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="pricing-page">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30" data-testid="launch-offer-banner">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-primary">Launch offer — 50% off for our first 50 customers</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Choose Your Plan</h1>
        <p className="text-muted-foreground mt-2">
          {currentRole === "free"
            ? "Upgrade to unlock wallet top-up and more features"
            : `You're on the ${currentRole.charAt(0).toUpperCase() + currentRole.slice(1)} plan`}
        </p>
      </div>

      {subscription?.status === "pending_cancellation" && (
        <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
          <p className="text-sm text-amber-300">Your subscription will be cancelled at the end of the current billing cycle.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Free Plan */}
        <PlanCard
          name="Free"
          price="0"
          planKey="free"
          features={PLAN_FEATURES.free}
          action={getPlanAction("free")}
          subscribing={subscribing}
          onAction={handlePlanAction}
        />

        {/* Dynamic plans from API */}
        {plans.map((plan) => {
          const meta = PLAN_FEATURES[plan.plan_key] || PLAN_FEATURES.beginner_monthly;
          return (
            <PlanCard
              key={plan.plan_key}
              name={plan.role.charAt(0).toUpperCase() + plan.role.slice(1)}
              price={String(plan.amount / 100)}
              basePrice={String(plan.base_amount / 100)}
              planKey={plan.plan_key}
              features={meta}
              action={getPlanAction(plan.plan_key)}
              subscribing={subscribing}
              onAction={handlePlanAction}
              credits={plan.credits_per_cycle}
            />
          );
        })}

        {/* Enterprise Card */}
        <div
          className="relative rounded-2xl border border-border p-6 flex flex-col"
          data-testid="pricing-enterprise-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Enterprise</h3>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            For marketing and agency teams and organizations that need custom plans, services, API, and more
          </p>

          <p className="text-3xl font-bold mb-8">Let's talk</p>

          <div className="mt-auto">
            <Button
              onClick={() => window.location.href = "/book-demo"}
              variant="secondary"
              className="w-full rounded-full h-11 text-sm font-medium"
              data-testid="pricing-enterprise-cta"
            >
              Book a Demo — Get 1 Free Ad
            </Button>
          </div>
        </div>
      </div>

      {(subscription?.status === "active" || subscription?.status === "authenticated") && (
        <div className="text-center mt-8">
          <Button
            variant="ghost"
            onClick={() => setConfirmModal({ type: "cancel" })}
            disabled={cancelling}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="cancel-subscription-btn"
          >
            {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Cancel Subscription
          </Button>
        </div>
      )}

      {/* Confirmation modal for upgrade / downgrade / cancel */}
      {confirmModal && (
        <ConfirmModal
          modal={confirmModal}
          onClose={() => setConfirmModal(null)}
          onConfirm={() => {
            const { type, targetPlanKey } = confirmModal;
            setConfirmModal(null);
            if (type === "cancel") {
              handleCancel();
            } else {
              handleChangePlan(targetPlanKey);
            }
          }}
          processing={cancelling || changingPlan}
        />
      )}
    </div>
  );
}

function PlanCard({ name, price, basePrice, planKey, features, action, subscribing, onAction, credits }) {
  const Icon = features.icon;
  const isPro = planKey === "pro_monthly";
  const isCurrent = action === "current";
  const isProcessing = subscribing === planKey;

  // CTA config based on action
  let ctaLabel = null;
  let ctaShortLabel = null;
  let ctaClass = "";
  let ctaIcon = null;
  let ctaTestId = null;
  let ctaEnabled = false;

  if (action === "subscribe") {
    ctaLabel = `Subscribe to ${name}`;
    ctaShortLabel = "Subscribe";
    ctaClass = isPro ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white";
    ctaTestId = `subscribe-${planKey}-btn`;
    ctaEnabled = true;
  } else if (action === "upgrade") {
    ctaLabel = `Upgrade to ${name}`;
    ctaShortLabel = "Upgrade";
    ctaClass = "bg-amber-600 hover:bg-amber-700 text-white";
    ctaIcon = <ArrowUpRight className="w-4 h-4 mr-1 shrink-0" />;
    ctaTestId = `upgrade-${planKey}-btn`;
    ctaEnabled = true;
  } else if (action === "downgrade") {
    ctaLabel = `Downgrade to ${name}`;
    ctaShortLabel = "Downgrade";
    ctaClass = "bg-slate-700 hover:bg-slate-600 text-white";
    ctaIcon = <ArrowDownRight className="w-4 h-4 mr-1 shrink-0" />;
    ctaTestId = `downgrade-${planKey}-btn`;
    ctaEnabled = true;
  }

  return (
    <div
      className={`relative rounded-xl border-2 ${isCurrent ? features.color : "border-border"} bg-card p-6 flex flex-col transition-all ${
        isPro ? "md:-mt-2 md:mb-2 shadow-lg shadow-amber-500/5" : ""
      }`}
      data-testid={`plan-card-${planKey}`}
    >
      {isPro && (
        <div className="absolute -top-3 left-4 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">
          POPULAR
        </div>
      )}

      {isCurrent && (
        <div className="absolute -top-3 right-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
          CURRENT
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 ${features.accent}`} />
        <h3 className="text-lg font-semibold">{name}</h3>
      </div>

      <div className="mb-1">
        {price !== "0" && (
          <div className="flex items-center gap-2 mb-1" data-testid={`launch-price-row-${planKey}`}>
            <span className="text-base text-muted-foreground line-through">
              ₹{(parseInt(basePrice || price) * 2).toLocaleString("en-IN")}
            </span>
            <span className="bg-primary/15 text-primary text-[11px] font-bold px-2 py-0.5 rounded-full">
              50% OFF · LAUNCH
            </span>
          </div>
        )}
        <span className="text-3xl font-bold">
          {price === "0" ? "Free" : `₹${parseInt(basePrice || price).toLocaleString("en-IN")}`}
        </span>
        {price !== "0" && <span className="text-sm text-muted-foreground"> + GST</span>}
        {price !== "0" && <span className="text-sm text-muted-foreground">/month</span>}
      </div>

      {credits && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-lg font-bold">{credits} credits/mo.</span>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>= {features.imageEquiv || Math.floor(credits / 2)} AI Image Generations</p>
            <p>~ {features.videoEquiv || Math.floor(credits / 6)} AI Video Ads</p>
          </div>
        </div>
      )}
      {!credits && planKey === "free" && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-slate-400" />
            <span className="text-lg font-bold">10 one-time credits</span>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>= 5 AI Image Generations</p>
            <p>~ 1 AI Video Ad</p>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-2.5 mb-6">
        {features.features.map((f, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
            <span>{f}</span>
          </div>
        ))}
        {features.notIncluded.map((f, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground/50">
            <X className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{f}</span>
          </div>
        ))}
      </div>

      {ctaEnabled && ctaLabel && (
        <Button
          onClick={() => onAction(planKey, action)}
          disabled={isProcessing}
          className={`w-full px-2 text-sm ${ctaClass}`}
          data-testid={ctaTestId}
          title={ctaLabel}
        >
          {isProcessing ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</>
          ) : (
            <>{ctaIcon}<span className="truncate">{ctaShortLabel || ctaLabel}</span></>
          )}
        </Button>
      )}
      {isCurrent && (
        <Button disabled className="w-full" variant="secondary" data-testid={`current-plan-${planKey}`}>
          Current Plan
        </Button>
      )}
      {action === "included" && (
        <Button disabled className="w-full" variant="outline">
          Included
        </Button>
      )}
    </div>
  );
}


function ConfirmModal({ modal, onClose, onConfirm, processing }) {
  const { type, targetPlanName, currentPlanName } = modal;
  const isCancel = type === "cancel";
  const isUpgrade = type === "upgrade";
  const isDowngrade = type === "downgrade";

  let title = "";
  let description = "";
  let bullets = [];
  let confirmLabel = "";
  let confirmClass = "";
  let testIdPrefix = "";
  let iconColor = "";

  if (isUpgrade) {
    title = `Upgrade to ${targetPlanName}?`;
    description = `You'll get instant ${targetPlanName} access. Your new billing cycle starts today.`;
    bullets = [
      `Your existing ${currentPlanName} plan will be cancelled immediately.`,
      `Remaining unused credits stay in your wallet.`,
      `${targetPlanName} credits are added on successful payment.`,
      `Next renewal charges happen on the same day of future months.`,
    ];
    confirmLabel = `Continue to Payment`;
    confirmClass = "bg-amber-600 hover:bg-amber-700 text-white";
    testIdPrefix = "upgrade";
    iconColor = "text-amber-400";
  } else if (isDowngrade) {
    title = `Downgrade to ${targetPlanName}?`;
    description = `You'll move to ${targetPlanName} immediately. You'll lose ${currentPlanName} features, and the remaining days of your current billing cycle are forfeited (no refund).`;
    bullets = [
      `Your ${currentPlanName} features end as soon as the new plan activates.`,
      `Remaining unused credits stay in your wallet.`,
      `${targetPlanName} credits are added on successful payment.`,
      `Next renewal charges happen on the same day of future months.`,
    ];
    confirmLabel = `Continue to Payment`;
    confirmClass = "bg-slate-700 hover:bg-slate-600 text-white";
    testIdPrefix = "downgrade";
    iconColor = "text-slate-400";
  } else if (isCancel) {
    title = `Cancel subscription?`;
    description = `You'll move to the Free tier immediately. The remaining days of your current billing cycle are forfeited (no refund).`;
    bullets = [
      `Your paid-tier features end immediately.`,
      `All your remaining credits stay in your wallet and can still be used.`,
      `No new monthly credits will be added.`,
      `You can resubscribe any time.`,
    ];
    confirmLabel = `Yes, Cancel Subscription`;
    confirmClass = "bg-red-600 hover:bg-red-700 text-white";
    testIdPrefix = "cancel";
    iconColor = "text-red-400";
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`${testIdPrefix}-confirm-modal`}
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0">
            <AlertTriangle className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
        </div>

        <ul className="space-y-2 mb-6">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={processing}
            className="flex-1"
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            Keep Current Plan
          </Button>
          <Button
            onClick={onConfirm}
            disabled={processing}
            className={`flex-1 ${confirmClass}`}
            data-testid={`${testIdPrefix}-confirm-btn`}
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
